<template>
  <div class="adm clinic-console">
    <div v-if="navOpen" class="adm-scrim" @click="navOpen = false"></div>

    <aside class="adm-side" :class="{ 'is-open': navOpen }" aria-label="Clinic navigation">
      <div class="adm-side__brand">
        <router-link to="/clinic-dashboard"><img src="@/assets/img/logo.png" alt="YourDoc" /></router-link>
        <span class="adm-side__tag">Clinic</span>
      </div>

      <div class="clinic-console__clinic">
        <span class="adm-avatar adm-tone-teal"><i class="fas fa-hospital"></i></span>
        <div class="clinic-console__clinic-text">
          <strong :title="clinic.clinicname">{{ clinic.clinicname || "Your clinic" }}</strong>
          <span :title="clinic.area">{{ clinic.area || " " }}</span>
        </div>
      </div>

      <nav class="adm-nav">
        <div class="adm-nav__group">Clinic</div>
        <router-link v-for="item in clinicNav" :key="item.to" :to="item.to" class="adm-nav__item"
                     :class="{ 'is-active': isActive(item.to) }" @click="navOpen = false">
          <i :class="item.icon"></i> {{ item.label }}
        </router-link>

        <template v-if="doctor.doctorId">
          <div class="adm-nav__group">Selected doctor</div>
          <div class="clinic-console__doctor">
            <span class="adm-avatar adm-avatar--sm">
              <img v-if="doctor.doctorImage" :src="doctor.doctorImage" alt="" />
              <template v-else>{{ initials(doctor.doctorName || "Dr") }}</template>
            </span>
            <div class="clinic-console__clinic-text">
              <strong>{{ doctor.doctorName ? `Dr. ${doctor.doctorName}` : "Doctor" }}</strong>
              <span>{{ doctor.specialization || "Tap Doctors to switch" }}</span>
            </div>
          </div>
          <router-link v-for="item in doctorNav" :key="item.to" :to="item.to" class="adm-nav__item"
                       :class="{ 'is-active': isActive(item.to) }" @click="navOpen = false">
            <i :class="item.icon"></i> {{ item.label }}
          </router-link>
        </template>
      </nav>

      <div class="adm-side__user">
        <span class="adm-avatar adm-avatar--sm adm-tone-violet">{{ initials(username) }}</span>
        <div class="adm-side__user-text">
          <strong>Signed in</strong>
          <span :title="username">{{ username || "…" }}</span>
        </div>
        <button type="button" class="adm-icon-btn" title="Sign out" aria-label="Sign out" @click="logout">
          <i class="fas fa-sign-out-alt"></i>
        </button>
      </div>
    </aside>

    <div class="adm-main">
      <header class="adm-topbar">
        <button type="button" class="adm-icon-btn adm-menu-btn" aria-label="Open menu" @click="navOpen = true">
          <i class="fas fa-bars"></i>
        </button>
        <router-link v-if="back" :to="back" class="adm-icon-btn" aria-label="Back"><i class="fas fa-arrow-left"></i></router-link>
        <div class="clinic-console__title">
          <h1 class="adm-topbar__title">{{ title }}</h1>
          <p v-if="subtitle" class="adm-topbar__subtitle">{{ subtitle }}</p>
        </div>
        <div class="adm-topbar__actions">
          <slot name="actions" />
        </div>
      </header>

      <main class="adm-content">
        <slot />
      </main>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { getCurrentUser, signOut } from "@/services/auth";
import "../../admin/admin.css";
import { initials } from "../../admin/admin-utils";

// The clinic header is the same on every page: fetch it once per session.
const CLINIC_KEY = "yourdoc.clinic.header";

export default {
  name: "ClinicLayout",
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    back: { type: String, default: "" },
  },
  data() {
    let clinic = {};
    try {
      clinic = JSON.parse(sessionStorage.getItem(CLINIC_KEY)) || {};
    } catch (e) {
      clinic = {};
    }
    return {
      navOpen: false,
      clinic,
      username: "",
      clinicNav: [
        { to: "/clinic-dashboard", label: "Doctors", icon: "fas fa-user-md" },
        { to: "/clinic-change-password", label: "Change password", icon: "fas fa-lock" },
      ],
      doctorNav: [
        { to: "/clinic-doctor-dashboard", label: "Appointments", icon: "fas fa-calendar-check" },
        { to: "/add-clinic-appointment", label: "Add appointment", icon: "fas fa-calendar-plus" },
        { to: "/clinic-appointment-history", label: "Appointment history", icon: "fas fa-history" },
        { to: "/clinic-available-timings", label: "Cancel appointments", icon: "fas fa-calendar-times" },
        { to: "/clinics-doctors-report", label: "Reports", icon: "fas fa-chart-bar" },
      ],
    };
  },
  computed: {
    doctor() {
      return this.$store.getters.getclinicDetails || {};
    },
  },
  methods: {
    initials,
    isActive(path) {
      return this.$route.path.replace(/\/$/, "") === path;
    },
    async loadClinic() {
      if (this.clinic.clinicname) return;
      try {
        const { data } = await axios.get(`/clinic/${sessionStorage.getItem("userdbid")}`);
        const first = (data.doctors || [])[0] || {};
        this.clinic = { clinicname: first.clinicname || "", area: first.area || "" };
        sessionStorage.setItem(CLINIC_KEY, JSON.stringify(this.clinic));
      } catch (error) {
        // the header just shows "Your clinic"
      }
    },
    async logout() {
      try {
        await signOut();
      } finally {
        sessionStorage.removeItem(CLINIC_KEY);
        this.$router.push("/doctor-login");
      }
    },
  },
  async created() {
    this.loadClinic();
    try {
      this.username = (await getCurrentUser()).username;
    } catch (error) {
      // the router guard handles signed-out users
    }
  },
  mounted() {
    document.body.classList.add("adm-page");
  },
  beforeUnmount() {
    document.body.classList.remove("adm-page");
  },
};
</script>

<style>
.clinic-console .adm-nav__item {
  text-decoration: none;
}

.clinic-console__clinic,
.clinic-console__doctor {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.clinic-console__clinic {
  padding: 14px 20px;
  border-bottom: 1px solid var(--adm-line-2);
}

.clinic-console__doctor {
  padding: 4px 12px 8px;
}

.clinic-console__clinic-text {
  min-width: 0;
}

.clinic-console__clinic-text strong {
  display: block;
  color: var(--adm-ink);
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clinic-console__clinic-text span {
  display: block;
  color: var(--adm-muted);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clinic-console__title {
  min-width: 0;
}

/* Older clinic pages still use the site theme's wrappers; inside the console
   they should not add the theme's big padding or fixed container width. */
.clinic-console .adm-content .content {
  padding: 0;
  min-height: 0;
}

.clinic-console .adm-content .content > .container,
.clinic-console .adm-content .content-space > .container {
  max-width: none;
  padding-left: 0;
  padding-right: 0;
}

.clinic-console .adm-content .main-wrapper {
  background: transparent;
}

.clinic-console .adm-content .card {
  border: 1px solid var(--adm-line);
  border-radius: var(--adm-radius);
  box-shadow: var(--adm-shadow);
}

.clinic-console .adm-content .btn-primary {
  background-color: var(--adm-primary);
  border-color: var(--adm-primary);
  color: #fff;
}

.clinic-console .adm-content .btn-primary:hover {
  background-color: var(--adm-primary-600);
  border-color: var(--adm-primary-600);
}

/* clinic-doctor-dashboard: the appointments tabs and table */
.clinic-console .clinic-appts {
  margin: 0;
  padding: 20px 8px;
}

/* clinic-changepassword */
.clinic-console .clinic-pw-card {
  max-width: 520px;
}

.clinic-console .clinic-pw-card .card-body {
  padding: 24px;
}

.clinic-console .clinic-pw-card .submit-btn {
  width: 100%;
}
</style>
